import { toISODate } from "@rumbledirect/ui-v2/day";
import { countIsoWeeks } from "@/lib/periodDate";

/**
 * How far ahead the planning looks when the user has not picked a period yet.
 * Half a year is the horizon a fleet manager can still act on: far enough to
 * see a cluster of returns coming, near enough to stay readable on one screen.
 */
const DEFAULT_WINDOW_MONTHS = 6;

/**
 * The longest period the backend will aggregate, mirrored here so an
 * over-long request is caught before it leaves the browser.
 */
export const MAX_PLANNING_WEEKS = 104;

/** The inclusive period the planning is aggregated over. */
export interface ContractPlanningPeriod {
  readonly from: string;
  readonly to: string;
}

/**
 * The period the planning opens on: from today up to half a year ahead.
 *
 * `today` is injectable so callers (and tests) can pin the window instead of
 * depending on the clock.
 */
export function defaultContractPlanningPeriod(
  today: Date = new Date(),
): ContractPlanningPeriod {
  const until = new Date(
    today.getFullYear(),
    today.getMonth() + DEFAULT_WINDOW_MONTHS,
    today.getDate(),
  );

  return { from: toISODate(today), to: toISODate(until) };
}

/**
 * Whether the period reads forwards. `YYYY-MM-DD` sorts chronologically as a
 * string, so no date parsing is needed.
 */
export function isPlanningPeriodOrdered(
  period: ContractPlanningPeriod,
): boolean {
  return period.from <= period.to;
}

/**
 * Whether the period stays inside the backend's week limit. The count is the
 * number of ISO weeks the request covers once the backend widens both bounds
 * to their week.
 *
 * Checking here keeps the failure legible: the backend answers an over-long
 * period with a validation error, which reads as a bug rather than as "your
 * period is too long".
 */
export function isPlanningPeriodWithinLimit(
  period: ContractPlanningPeriod,
): boolean {
  return countIsoWeeks(period.from, period.to) <= MAX_PLANNING_WEEKS;
}
